const MARCADORES_MOJIBAKE = /(?:\u00c3.|\u00c2.|\u00e2.|\u00f0\u0178.|\ufffd)/u;
const MARCADORES_MOJIBAKE_GLOBAL = /(?:\u00c3.|\u00c2.|\u00e2.|\u00f0\u0178.|\ufffd)/gu;
const decoderUtf8 = new TextDecoder("utf-8", { fatal: false });
const atributosTextuais = ["title", "aria-label", "placeholder", "alt", "data-tooltip"] as const;
const MAPA_UNICODE_PARA_CP1252 = new Map<number, number>([
  [0x20ac, 0x80],
  [0x201a, 0x82],
  [0x0192, 0x83],
  [0x201e, 0x84],
  [0x2026, 0x85],
  [0x2020, 0x86],
  [0x2021, 0x87],
  [0x02c6, 0x88],
  [0x2030, 0x89],
  [0x0160, 0x8a],
  [0x2039, 0x8b],
  [0x0152, 0x8c],
  [0x017d, 0x8e],
  [0x2018, 0x91],
  [0x2019, 0x92],
  [0x201c, 0x93],
  [0x201d, 0x94],
  [0x2022, 0x95],
  [0x2013, 0x96],
  [0x2014, 0x97],
  [0x02dc, 0x98],
  [0x2122, 0x99],
  [0x0161, 0x9a],
  [0x203a, 0x9b],
  [0x0153, 0x9c],
  [0x017e, 0x9e],
  [0x0178, 0x9f],
]);

let corretorGlobalInstalado = false;

function contarMarcadoresMojibake(valor: string): number {
  const ocorrencias = valor.match(MARCADORES_MOJIBAKE_GLOBAL);
  return ocorrencias ? ocorrencias.length : 0;
}

function decodificarComoUtf8(valor: string): string {
  const bytes = Uint8Array.from(Array.from(valor), (char) => {
    const codePoint = char.codePointAt(0) ?? 0x3f;
    if (codePoint <= 0xff) return codePoint;
    return MAPA_UNICODE_PARA_CP1252.get(codePoint) ?? 0x3f;
  });
  return decoderUtf8.decode(bytes);
}

function corrigirSequenciasComuns(valor: string): string {
  return valor
    .replace(/\u00e2\u20ac\u00a2/g, "\u2022")
    .replace(/\u00e2\u20ac\u201d/g, "\u2014")
    .replace(/\u00e2\u20ac\u201c/g, "\u2013")
    .replace(/\u00e2\u20ac\u2122/g, "\u2019")
    .replace(/\u00e2\u20ac\u0153/g, "\u201c")
    .replace(/\u00e2\u20ac/g, "\"");
}

export function normalizarTextoPtBr(valor: unknown): string {
  const texto = String(valor ?? "");
  let atual = corrigirSequenciasComuns(texto);

  if (!MARCADORES_MOJIBAKE.test(atual)) return atual;

  let scoreAtual = contarMarcadoresMojibake(atual);

  for (let tentativa = 0; tentativa < 4; tentativa += 1) {
    const candidato = corrigirSequenciasComuns(decodificarComoUtf8(atual));
    const scoreCandidato = contarMarcadoresMojibake(candidato);

    if (scoreCandidato >= scoreAtual) break;

    atual = candidato;
    scoreAtual = scoreCandidato;
    if (scoreAtual === 0) break;
  }

  return atual;
}

export function normalizarListaPtBr(valores: readonly unknown[]): string[] {
  return valores.map((valor) => normalizarTextoPtBr(valor));
}

export function normalizarMatrizPtBr(linhas: readonly (readonly unknown[])[]): string[][] {
  return linhas.map((linha) => normalizarListaPtBr(linha));
}

function deveIgnorarTexto(no: Node): boolean {
  const pai = no.parentElement;
  if (!pai) return false;

  return Boolean(pai.closest('script, style, textarea, input, [contenteditable="true"]'));
}

function normalizarTextoNo(no: Text) {
  if (deveIgnorarTexto(no)) return;
  const atual = no.nodeValue ?? "";
  const normalizado = normalizarTextoPtBr(atual);
  if (normalizado !== atual) {
    no.nodeValue = normalizado;
  }
}

function normalizarAtributosElemento(elemento: Element) {
  atributosTextuais.forEach((atributo) => {
    const atual = elemento.getAttribute(atributo);
    if (!atual) return;
    const normalizado = normalizarTextoPtBr(atual);
    if (normalizado !== atual) {
      elemento.setAttribute(atributo, normalizado);
    }
  });
}

function normalizarArvoreTexto(raiz: Node) {
  if (raiz.nodeType === Node.TEXT_NODE) {
    normalizarTextoNo(raiz as Text);
    return;
  }

  if (raiz.nodeType === Node.ELEMENT_NODE) {
    normalizarAtributosElemento(raiz as Element);
  }

  const walker = document.createTreeWalker(raiz, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
  let atual: Node | null = walker.currentNode;

  while (atual) {
    if (atual.nodeType === Node.TEXT_NODE) {
      normalizarTextoNo(atual as Text);
    } else if (atual.nodeType === Node.ELEMENT_NODE) {
      normalizarAtributosElemento(atual as Element);
    }
    atual = walker.nextNode();
  }
}

export function instalarCorretorGlobalMojibake() {
  if (typeof window === "undefined" || typeof document === "undefined" || corretorGlobalInstalado) return;
  corretorGlobalInstalado = true;

  const aplicar = () => {
    if (document.title) {
      document.title = normalizarTextoPtBr(document.title);
    }
    if (document.body) {
      normalizarArvoreTexto(document.body);
    }
  };

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === "characterData" && mutation.target.nodeType === Node.TEXT_NODE) {
        normalizarTextoNo(mutation.target as Text);
        return;
      }

      if (mutation.type === "attributes" && mutation.target.nodeType === Node.ELEMENT_NODE) {
        normalizarAtributosElemento(mutation.target as Element);
        return;
      }

      mutation.addedNodes.forEach((no) => normalizarArvoreTexto(no));
    });
  });

  const iniciar = () => {
    aplicar();
    if (!document.body) return;
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: [...atributosTextuais],
    });
  };

  if (document.body) {
    iniciar();
  } else {
    window.addEventListener("DOMContentLoaded", iniciar, { once: true });
  }
}
